import type { RecentSearchEntry, SearchScope } from "types/search";

const LOCAL_STORAGE_KEY = "recent_searches";
const MAX_RECENT_SEARCHES = 12;

const isSearchScope = (value: unknown): value is SearchScope =>
  value === "all" ||
  value === "issues" ||
  value === "posts" ||
  value === "comments" ||
  value === "actors" ||
  value === "tags";

const readLocalStorage = (key: string): string | null => {
  try {
    if (typeof window === "undefined" || !("localStorage" in window)) {
      return null;
    }

    return window.localStorage.getItem(key);
  } catch (error) {
    console.error("Error reading localStorage:", error);
    return null;
  }
};

const writeLocalStorage = (key: string, value: string): void => {
  try {
    if (typeof window === "undefined" || !("localStorage" in window)) {
      return;
    }

    window.localStorage.setItem(key, value);
  } catch (error) {
    console.error("Error writing localStorage:", error);
  }
};

export const getRecentSearches = (): RecentSearchEntry[] => {
  try {
    const rawValue = readLocalStorage(LOCAL_STORAGE_KEY);

    if (!rawValue) {
      return [];
    }

    const parsedValue = JSON.parse(rawValue);

    if (!Array.isArray(parsedValue)) {
      return [];
    }

    return parsedValue
      .flatMap((entry): RecentSearchEntry[] => {
        if (!entry || typeof entry !== "object") {
          return [];
        }

        const query = typeof entry.query === "string" ? entry.query.trim() : "";
        const scope = entry.scope;
        const createdAt =
          typeof entry.createdAt === "number" && Number.isFinite(entry.createdAt)
            ? entry.createdAt
            : Date.now();

        if (!query || !isSearchScope(scope)) {
          return [];
        }

        return [
          {
            id:
              typeof entry.id === "string" && entry.id.length > 0
                ? entry.id
                : `${query.toLowerCase()}::${scope}`,
            query,
            scope,
            createdAt,
          },
        ];
      })
      .sort((left, right) => right.createdAt - left.createdAt);
  } catch (error) {
    console.error("Error parsing recent searches:", error);
    return [];
  }
};

export const addRecentSearch = (query: string, scope: SearchScope = "all"): void => {
  const normalizedQuery = query.trim().replace(/\s+/g, " ");

  if (!normalizedQuery) {
    return;
  }

  const existingEntries = getRecentSearches().filter(
    (entry) => !(entry.query.toLowerCase() === normalizedQuery.toLowerCase() && entry.scope === scope)
  );

  const nextEntry: RecentSearchEntry = {
    id: `${normalizedQuery.toLowerCase()}::${scope}`,
    query: normalizedQuery,
    scope,
    createdAt: Date.now(),
  };

  writeLocalStorage(
    LOCAL_STORAGE_KEY,
    JSON.stringify([nextEntry, ...existingEntries].slice(0, MAX_RECENT_SEARCHES))
  );
};

export const removeRecentSearch = (entryId: string): void => {
  const nextEntries = getRecentSearches().filter((entry) => entry.id !== entryId);
  writeLocalStorage(LOCAL_STORAGE_KEY, JSON.stringify(nextEntries));
};

export const clearRecentSearches = (): void => {
  writeLocalStorage(LOCAL_STORAGE_KEY, JSON.stringify([]));
};
